// What Tenders needs from the app that hosts it (#216), handed over once at
// bootstrap — the same pattern as core's registerHttpClients and
// datacentral-react's configureDataHooks. The host calls createTendersModule
// from src/app/register-tenders.tsx; nothing here imports the app.
//
// The generic half (modals, the session) is the shell's host contract. Tenders
// has no component, link or permission to ask for: only the i18n instance, to
// register its `tenders` namespace on.
import type {
    HostModalApi,
    HostSession,
} from '@vnetwork/datacentral-shell/host'
import vi from './locales/vi.json'
import en from './locales/en.json'

/** The signed-in employee, as far as Tenders reads it. */
export interface TendersEmployee {
    employee_id: number
}

export interface TendersHost
    extends HostModalApi,
        HostSession<TendersEmployee> {
    /**
     * The app's i18next instance, already initialised. The `tenders`
     * namespace is registered against it here. A package must not import
     * i18next itself to add bundles: that could hit an instance before the
     * app's init (#59 hazard 5).
     */
    i18n: {
        addResourceBundle: (
            lng: string,
            ns: string,
            resources: object
        ) => unknown
    }
}

let current: TendersHost | undefined

export function registerTendersHost(host: TendersHost): void {
    current = host
    host.i18n.addResourceBundle('vi', 'tenders', vi)
    // `en` was never registered, so the English UI showed the vi sidebar label.
    // Keys en lacks fall back to vi (fallbackLng).
    host.i18n.addResourceBundle('en', 'tenders', en)
}

/** The registered host. Read it at render or call time, never at import. */
function getTendersHost(): TendersHost {
    if (!current)
        throw new Error(
            '[tenders] no host registered. Call createTendersModule({ ...hostBase, i18n }) at app bootstrap (src/app/register-tenders.tsx).'
        )
    return current
}

// The host's hooks, under the names the app's own hooks have, so call sites
// read as before. Each reads the host at call time, inside a component.
export const useModal = () => getTendersHost().useModal()
export const useCurrentEmployee = () => getTendersHost().useCurrentEmployee()
